/**
 * Extração de ID + metadados do YouTube via oEmbed (sem API key, sem backend).
 * O ID é validado contra o mesmo formato do constraint do banco (defesa em
 * profundidade contra injeção no player/iframe).
 */

import { withTimeout } from '../../../utils/community/async';

const YOUTUBE_ID_RE =
  /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

const VALID_ID_RE = /^[A-Za-z0-9_-]{11}$/;

// O fetch do RN não tem timeout nativo. Sem teto, uma resposta que estola deixa o
// compositor com o spinner girando para sempre e o botão "Publicar" desabilitado —
// o usuário toca e nada acontece, sem explicação. Era o único ponto de rede do
// módulo sem proteção (todos os outros já usam `withTimeout`).
const OEMBED_TIMEOUT_MS = 10000;

/** @returns {string|null} o videoId de 11 chars, ou null se a URL não for válida. */
export const extractYoutubeId = (urlOrId) => {
  const raw = String(urlOrId || '').trim();
  if (VALID_ID_RE.test(raw)) return raw; // já é um id
  const match = raw.match(YOUTUBE_ID_RE);
  return match ? match[1] : null;
};

/**
 * Busca título/thumbnail via oEmbed público do YouTube.
 * @param {string} youtubeId
 * @returns {Promise<{title:string, thumbnailUrl:string|null, authorName:string|null}>}
 */
export const fetchYoutubeMetadata = async (youtubeId) => {
  if (!VALID_ID_RE.test(youtubeId)) {
    throw new Error('ID de vídeo do YouTube inválido');
  }
  const target = `https://www.youtube.com/watch?v=${youtubeId}`;
  const res = await withTimeout(
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(target)}&format=json`),
    OEMBED_TIMEOUT_MS,
    'O YouTube demorou a responder. Verifique sua conexão e tente de novo.',
  );
  if (!res.ok) {
    // 401/404 = vídeo privado/removido/embed desabilitado
    throw new Error('Não foi possível obter os dados deste vídeo do YouTube');
  }
  const data = await res.json();
  return {
    title: data.title || 'Vídeo',
    thumbnailUrl: data.thumbnail_url || `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`,
    authorName: data.author_name || null,
  };
};
